// release-plugin-hold-slide-audio: a plugin slide type that plays its own audio holds the host
// audio (pauseHostAudio xprop) and releases it (resumeHostAudio). With the flag on, that hold also
// covers the slide audio (the title-bar audio SlideTitleAudioPlayer plays, slideHTMLAudioElement):
// it is paused while held, cannot be started from autoplay, the P hotkey or the presenting pill,
// and comes back when the plugin releases. audio.module and the two players hook in here behind
// the flag; with it off none of this runs.
import EventBus from '@/eventBus'

// SlideTitleAudioPlayer listens for this: the plugin released a hold that kept its slide audio
// from ever starting, so it autoplays now if its own rules allow (audioAutoPlay, quiz screen…).
export const PLUGIN_HOLD_RELEASED_EVENT = 'plugin-hold-slide-audio-released'

// Whether the slide audio was audible when the current hold paused it. Written by the first
// pauseHostAudio of every hold, so a hold that ended without a release (navigation, stop
// presenting) can never leave a stale reading for the next one.
let slideAudioPlayingWhenHeld = false

export const isSlideAudioHoldEnabled = (store) => {
  if (store.getters['featureToggles/canUsePluginHoldSlideAudio']) return true
  return false
}

// True while a plugin holds the host audio and the flag is on.
export const isSlideAudioHeldByPlugin = (store) => {
  if (store.getters['featureToggles/canUsePluginHoldSlideAudio']) return Boolean(store.getters['audio/isHostAudioHeldByPlugin'])
  return false
}

// The presenting pill is attached to the held slide audio and it is not playing: hide it, so the
// presenter does not start the slide audio over the plugin's own. The deck-music pill stays (a
// manual play there still beats the plugin).
export const isPillHiddenByPluginHold = (store, slideAudio, isPlaying) => Boolean(slideAudio)
  && !isPlaying
  && isSlideAudioHeldByPlugin(store)

// Starts the slide audio unless a plugin holds it. Replaces SlideTitleAudioPlayer's direct
// play() calls; with the flag off it is exactly that play().
export const playSlideAudioUnlessHeld = (slideAudio, store) => {
  if (isSlideAudioHeldByPlugin(store)) return
  slideAudio.play().catch(() => {})
}

// pauseHostAudio: pause the slide audio too. A repeated call in the same hold finds it already
// paused, so only the first one reads whether it was playing.
export const holdSlideAudio = (slideAudio, isRepeatedHold) => {
  if (!isRepeatedHold) slideAudioPlayingWhenHeld = Boolean(slideAudio) && !slideAudio.paused
  if (slideAudio) slideAudio.pause()
}

// The same gates SlideTitleAudioPlayer applies before it plays: presenting, not backstage, and
// this PowerPoint frame is the one on stage (a hold can outlive a PowerPoint focus change, which
// clears neither the hold nor the reading).
const canSlideAudioPlayHere = (rootGetters) => Boolean(rootGetters.isPresenting)
  && !rootGetters.isBackstage
  && !rootGetters.isPptFrameStoodDown
  && !rootGetters.isPptEditSlideNotSelected

// resumeHostAudio: the slide audio is next in line after the plugin's own audio. It comes back if
// the hold interrupted it, or autoplays (through SlideTitleAudioPlayer) if the hold kept it from
// ever starting. One the presenter paused stays paused: it has played, and was not playing.
export const releaseSlideAudio = (slideAudio, rootGetters) => {
  const wasPlaying = slideAudioPlayingWhenHeld
  slideAudioPlayingWhenHeld = false
  if (!slideAudio) return
  if (wasPlaying) {
    if (canSlideAudioPlayHere(rootGetters)) slideAudio.play()?.catch(() => {})
    return
  }
  if (!slideAudio.played?.length) EventBus.$emit(PLUGIN_HOLD_RELEASED_EVENT)
}
